"use client";

import { useState, useEffect } from "react";
import { Menu, Phone, Zap } from "lucide-react";
import { Link } from "@/components/common/Link";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
function DesktopNav() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="hidden md:flex gap-8">
        {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="h-4 w-16 bg-muted/20 animate-pulse rounded-md" />)}
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden md:flex">
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="font-default tracking-tight text-sm font-semibold text-foreground hover:text-primary transition-colors">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileNav() {
  const {
    menuItems,
    loading
  } = useMenu();
  const [isOpen, setIsOpen] = useState(false);
  if (loading) return null;
  return <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon" className="md:hidden text-foreground hover:bg-foreground/10">
          <Menu className="size-6" />
          <span className="sr-only">Toggle menu</span>
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="w-full sm:w-[400px] bg-background border-l border-border/20 p-8">
        <div className="flex flex-col gap-8 mt-12">
          {menuItems.map((item, i) => <div key={item.id} data-index={i}>
              <Link to={item.href || "#"} className="font-default tracking-tight text-2xl font-bold text-foreground hover:text-primary transition-colors block" onClick={() => setIsOpen(false)}>
                {item.label}
              </Link>
            </div>)}
          <div className="mt-8 pt-8 border-t border-border/20">
            <Button variant="outline" className="w-full bg-ring text-primary-foreground hover:bg-ring/90 font-default font-bold tracking-tight uppercase text-sm h-12 rounded-md shadow-lg" onClick={() => setIsOpen(false)}>
              24/7 EMERGENCY
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <>
      <div className="sticky top-0 z-40 w-full bg-ring text-primary-foreground py-3 px-4 text-center text-xs md:text-sm font-bold flex items-center justify-center gap-2 shadow-md">
        <Zap className="size-4 fill-current" />
        <span>ACTIVE EMERGENCY RESPONSE — Pueblo County — Average arrival 45 min or less — Call 24/7</span>
      </div>
      <section data-section-id="264695" id="header" className={`sticky top-0 z-50 w-full transition-all duration-300 bg-background text-foreground ${isScrolled ? "bg-background/80 backdrop-blur-xl border-b border-border/40 shadow-md" : "border-b border-border/10"}`}>
        <MenuProvider menu_id="16">
          <div className="container mx-auto px-6 h-[80px] flex items-center justify-between">
            <div className="flex-1 flex justify-start">
              <Link to="/HomePage" className="flex-shrink-0">
                <WvcLogo className="h-8 w-auto text-primary" />
              </Link>
            </div>

            <div className="flex-shrink-0">
              <DesktopNav />
            </div>

            <div className="flex-1 flex justify-end items-center gap-4">
              <div className="hidden lg:flex flex-col items-end mr-4">
                <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Emergency Dispatch</span>
                <Button variant="link" className="p-0 h-auto font-mono text-sm font-bold text-foreground hover:text-ring">
                  [CALL NOW]
                </Button>
              </div>
              <Button variant="outline" className="hidden md:inline-flex bg-ring border-ring text-primary-foreground hover:bg-ring/90 font-default font-bold tracking-tight uppercase text-xs px-6 h-10 rounded-md shadow-md transition-all">
                24/7 EMERGENCY
              </Button>
              <MobileNav />
              <Button className="md:hidden fixed bottom-6 right-6 z-50 rounded-lg bg-ring text-primary-foreground font-bold shadow-lg px-6 h-12 flex items-center gap-2">
                <Phone className="size-4" />
                Call 24/7 Emergency Line
              </Button>
            </div>
          </div>
        </MenuProvider>
      </section>
    </>;
}